import Axios from "axios";

const coreApiBaseUrl =
  import.meta.env.VITE_CORE_API_URL || `${import.meta.env.VITE_BASE_URL}/api/v1`;

export type CommuteMode = "bus" | "train";

/** Hand-off payload between the results grid and the checkout page. */
export const COMMUTE_CHECKOUT_KEY = "commute_checkout_state_v1";
export const COMMUTE_SUCCESS_KEY = "commute_checkout_success_v1";
export const COWRY_FUND_SUCCESS_KEY = "cowry_fund_success_v1";

export interface CommuteService {
  serviceId: string;
  mode: CommuteMode;
  name: string;
  /** Every route on the service departs from here. */
  pickup: string;
  operator: string;
  vehicleInfo: string;
  corridor: string;
  destinationCount: number;
  fareFrom: number;
  fareTo: number;
  currency: string;
}

export interface CommuteRoute {
  routeId: string;
  pickup: string;
  destination: string;
  fare: number;
  currency: string;
  durationMinutes: number;
  stops: number;
}

export type CommuteServiceRoutes = CommuteService & { routes: CommuteRoute[] };

export interface CowryCard {
  cardNumber: string;
  maskedCardNumber: string;
  valid: boolean;
  registered: boolean;
  holderName: string | null;
  balance: number;
  currency: string;
  status: "active" | "blocked" | "unregistered";
  lastFundedAt: number | null;
  message: string;
}

export interface CommuteTicket {
  ticketRef: string;
  mode: CommuteMode;
  service: CommuteService;
  route: Omit<CommuteRoute, "currency">;
  passengers: number;
  fare: number;
  currency: string;
  card: {
    cardNumber: string;
    maskedCardNumber: string;
    holderName: string | null;
    balanceBefore: number;
    balanceAfter: number;
  };
  paymentReference: string;
  /** PNG data URI, ready for an <img src>. */
  qrCode: string;
  qrPayload: string;
  issuedAt: number;
  expiresAt: number;
  message: string;
}

export interface CowryFundReceipt {
  reference: string;
  cardNumber: string;
  maskedCardNumber: string;
  holderName: string | null;
  amount: number;
  currency: string;
  balanceBefore: number;
  balanceAfter: number;
  newlyRegistered: boolean;
  fundedAt: number;
  message: string;
}

export interface CowryCardTransaction {
  id: string;
  type: "fund" | "fare";
  direction: "credit" | "debit";
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  reference: string;
  description: string | null;
  createdAt: number;
}

/** Responses arrive as `{ success, data }`; peel that wrapper off. */
const unwrap = <T,>(payload: unknown): T => {
  const body = payload as { data?: unknown } | undefined;
  return (body?.data !== undefined ? body.data : payload) as T;
};

/** Surfaces the API's own message, which is what the commuter needs to read. */
export const commuteError = (err: unknown, fallback: string): string => {
  const message = (
    err as { response?: { data?: { message?: unknown } } } | undefined
  )?.response?.data?.message;

  // class-validator returns an array of failures; the API returns a string.
  if (Array.isArray(message)) {
    return message.filter((item) => typeof item === "string").join(", ");
  }
  if (typeof message === "string" && message) return message;
  return fallback;
};

const modePath = (mode: CommuteMode) =>
  mode === "train" ? "/transport/train" : "/transport/bus/lagbus";

export const formatNaira = (amount: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount || 0);

export const formatCommuteTime = (unixSeconds?: number) => {
  if (!unixSeconds) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(unixSeconds * 1000));
};

export async function listCommuteServices(
  mode: CommuteMode,
): Promise<CommuteService[]> {
  const segment = mode === "train" ? "trains" : "buses";
  const res = await Axios.get(`${coreApiBaseUrl}${modePath(mode)}/${segment}`);
  const body = unwrap<{ data: CommuteService[] }>(res.data);
  return body?.data || [];
}

export async function getCommuteRoutes(
  mode: CommuteMode,
  serviceId: string,
): Promise<CommuteServiceRoutes> {
  const segment = mode === "train" ? "trains" : "buses";
  const res = await Axios.get(
    `${coreApiBaseUrl}${modePath(mode)}/${segment}/${encodeURIComponent(
      serviceId,
    )}/routes`,
  );
  return unwrap<CommuteServiceRoutes>(res.data);
}

export async function payCommuteFare(
  mode: CommuteMode,
  payload: {
    serviceId: string;
    routeId: string;
    cardNumber: string;
    passengers?: number;
  },
): Promise<CommuteTicket> {
  const res = await Axios.post(
    `${coreApiBaseUrl}${modePath(mode)}/pay`,
    payload,
  );
  return unwrap<CommuteTicket>(res.data);
}

export async function lookupCowryCard(cardNumber: string): Promise<CowryCard> {
  const res = await Axios.post(
    `${coreApiBaseUrl}/transport/cowry/cards/lookup`,
    { cardNumber },
  );
  return unwrap<CowryCard>(res.data);
}

export async function fundCowryCard(payload: {
  cardNumber: string;
  amount: number;
  holderName?: string;
}): Promise<CowryFundReceipt> {
  const res = await Axios.post(
    `${coreApiBaseUrl}/transport/cowry/cards/fund`,
    payload,
  );
  return unwrap<CowryFundReceipt>(res.data);
}

export async function getCowryCardHistory(
  cardNumber: string,
): Promise<CowryCardTransaction[]> {
  const res = await Axios.get(
    `${coreApiBaseUrl}/transport/cowry/cards/${encodeURIComponent(
      cardNumber,
    )}/transactions?page=1&limit=20`,
  );
  const body = unwrap<{ data: CowryCardTransaction[] }>(res.data);
  return body?.data || [];
}

/**
 * Mirrors the API's card-number rule so the app only calls the lookup once a
 * full number has been typed. Returns the canonical 11-digit form, or null.
 */
export function normalizeCardNumber(raw: string): string | null {
  const digits = String(raw || "").replace(/\D/g, "");

  let msisdn = digits;
  if (msisdn.startsWith("234")) {
    msisdn = `0${msisdn.slice(3)}`;
  } else if (msisdn.length === 10 && !msisdn.startsWith("0")) {
    msisdn = `0${msisdn}`;
  }

  return /^0[789][01]\d{8}$/.test(msisdn) ? msisdn : null;
}
